import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  DataPoint,
  GetDataPointsByVehicleAndDate,
  GetLastDataPointsByCompanyAndDate,
  LastDataPointsVehicles,
} from "./geolocationApi";

export interface GeolocationState {
  dataPointsByVehicleStatus: QueryStatus;
  lastDataPointsVehiclesStatus: QueryStatus;
  lastDataPointsVehicles: LastDataPointsVehicles | null;
}

const initialState: GeolocationState = {
  dataPointsByVehicleStatus: "idle",
  lastDataPointsVehiclesStatus: "idle",
  lastDataPointsVehicles: null,
};

export const getDataPointsByVehicleAndDateAsync = createAsyncThunk(
  "getDataPointsByVehicleAndDateAsync/call",
  async (payload: GetDataPointsByVehicleAndDate, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;

    const response = await authAxios({
      companyUuid: company?.uuid,
    }).get<DataPoint[]>(`trips/vehicles/${payload.vehicleUuid}/locations`, {
      params: {
        date: payload.date,
      },
    });

    thunkAPI.dispatch(
      geolocationSlice.actions.setDataPointsForVehicle({
        vehicleUuid: payload.vehicleUuid,
        dataPoint: response.data,
      }),
    );
  },
);

export const getLastDataPointsByCompanyAndDateAsync = createAsyncThunk(
  "getLastDataPointsByCompanyAndDateAsync/call",
  async (payload: GetLastDataPointsByCompanyAndDate) => {
    const response = await authAxios({
      companyUuid: payload.companyUuid,
    }).get<LastDataPointsVehicles>(
      `trips/company/${payload.companyUuid}/vehicles/last_location`,
      {
        params: {
          date: payload.date,
        },
      },
    );
    return response.data;
  },
);

export const geolocationSlice = createSlice({
  name: "geolocation",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetDataPointsByVehicleStatus(state) {
      state.dataPointsByVehicleStatus = "idle";
    },
    resetLastDataPointsVehiclesStatus(state) {
      state.lastDataPointsVehiclesStatus = "idle";
    },
    setDataPointsForVehicle(
      state,
      action: PayloadAction<{ vehicleUuid: string; dataPoint: DataPoint[] }>,
    ) {
      if (!state.lastDataPointsVehicles) return;
      state.lastDataPointsVehicles = state.lastDataPointsVehicles.map(
        (vehicle) =>
          vehicle.vehicleUuid === action.payload.vehicleUuid
            ? {
                ...vehicle,
                dataPoints: action.payload.dataPoint,
              }
            : vehicle,
      );
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getDataPointsByVehicleAndDateAsync.pending, (state) => {
        state.dataPointsByVehicleStatus = "processing";
      })
      .addCase(getDataPointsByVehicleAndDateAsync.fulfilled, (state) => {
        state.dataPointsByVehicleStatus = "success";
      })
      .addCase(getDataPointsByVehicleAndDateAsync.rejected, (state) => {
        state.dataPointsByVehicleStatus = "failed";
      })
      .addCase(getLastDataPointsByCompanyAndDateAsync.pending, (state) => {
        state.lastDataPointsVehiclesStatus = "processing";
      })
      .addCase(
        getLastDataPointsByCompanyAndDateAsync.fulfilled,
        (state, action) => {
          state.lastDataPointsVehicles = action.payload;
          state.lastDataPointsVehiclesStatus = "success";
        },
      )
      .addCase(getLastDataPointsByCompanyAndDateAsync.rejected, (state) => {
        state.lastDataPointsVehiclesStatus = "failed";
      });
  },
});

export const selectDataPointsByVehicleStatus = (state: RootState) =>
  state.geolocation.dataPointsByVehicleStatus;
export const selectLastDataPointsVehicles = (state: RootState) =>
  state.geolocation.lastDataPointsVehicles;
export const selectLastDataPointsVehiclesStatus = (state: RootState) =>
  state.geolocation.lastDataPointsVehiclesStatus;

export default geolocationSlice.reducer;
